import React, { useState, useEffect } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import {
  Card,
  Button,
  Modal,
  Form,
  Badge,
  Alert,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';

export default function BookingLinkManager() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingLink, setDeletingLink] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [qrCodes, setQrCodes] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    maxUses: 1,
    prefillData: {
      from: '',
      to: '',
      notes: '',
      whatToExpect: ''
    }
  });

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/booking-links');
      setLinks(data);
    } catch (err) {
      console.error('Error loading booking links:', err);
      toast.error('Failed to load booking links');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/booking-links', formData);
      toast.success('Booking link created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', maxUses: 1, prefillData: { from: '', to: '', notes: '', whatToExpect: '' } });
      loadLinks();
    } catch (err) {
      console.error('Error creating booking link:', err);
      toast.error('Failed to create booking link');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/booking-links/${editingLink._id}`, formData);
      toast.success('Booking link updated successfully');
      setShowEditModal(false);
      setEditingLink(null);
      setFormData({ name: '', maxUses: 1, prefillData: { from: '', to: '', notes: '', whatToExpect: '' } });
      loadLinks();
    } catch (err) {
      console.error('Error updating booking link:', err);
      toast.error('Failed to update booking link');
    }
  };

  const handleDelete = async () => {
    if (!deletingLink) return;

    try {
      await axios.delete(`/booking-links/${deletingLink._id}`);
      toast.success('Booking link deleted successfully');
      setShowDeleteModal(false);
      setDeletingLink(null);
      loadLinks();
    } catch (err) {
      console.error('Error deleting booking link:', err);
      toast.error('Failed to delete booking link');
    }
  };

  const openDeleteModal = (link) => {
    setDeletingLink(link);
    setShowDeleteModal(true);
  };

  const openEditModal = (link) => {
    setEditingLink(link);
    setFormData({
      name: link.name,
      maxUses: link.maxUses,
      prefillData: link.prefillData || { from: '', to: '', notes: '', whatToExpect: '' }
    });
    setShowEditModal(true);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard!');
    }
  };

  const getFullLink = (linkId) => {
    return `${window.location.origin}/book/${linkId}`;
  };

  const getStatusBadge = (link) => {
    if (!link.isActive) {
      return <Badge bg="secondary">Inactive</Badge>;
    }
    if (link.currentUses >= link.maxUses) {
      return <Badge bg="warning">Used Up</Badge>;
    }
    return <Badge bg="success">Active</Badge>;
  };

  const generateQRCode = async (linkId) => {
    try {
      const url = getFullLink(linkId);
      const qrCodeDataURL = await QRCode.toDataURL(url, {
        width: 150,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodes(prev => ({
        ...prev,
        [linkId]: qrCodeDataURL
      }));
      return qrCodeDataURL;
    } catch (err) {
      console.error('Error generating QR code:', err);
      toast.error('Failed to generate QR code');
      return null;
    }
  };

  const copyQRCodeToClipboard = async (linkId) => {
    try {
      let qrCodeDataURL = qrCodes[linkId];
      if (!qrCodeDataURL) {
        qrCodeDataURL = await generateQRCode(linkId);
      }
      
      if (qrCodeDataURL) {
        // Convert data URL to blob
        const response = await fetch(qrCodeDataURL);
        const blob = await response.blob();
        
        // Copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob
          })
        ]);
        
        toast.success('QR code copied to clipboard!');
      }
    } catch (err) {
      console.error('Failed to copy QR code:', err);
      toast.error('Failed to copy QR code to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" />
        <p className="mt-2">Loading booking links...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>My Booking Links</h2>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          Create New Link
        </Button>
      </div>

      <Alert variant="info">
        <strong>How it works:</strong> Create unique booking links that you can share with customers. 
        When a customer uses your link, the booking will appear in your diary for you to review and accept.
        Each link can be used a limited number of times. You can also generate QR codes for easy sharing and printing.
      </Alert>

      {links.length === 0 ? (
        <Card>
          <Card.Body className="text-center">
            <p className="text-muted">No booking links created yet.</p>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Create Your First Link
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="row">
          {links.map((link) => (
            <div key={link._id} className="col-12 col-md-6 col-lg-4 mb-3">
              <Card className="h-100">
                <Card.Body>
                  {/* Link Name and Status */}
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div 
                      className="fw-bold text-primary flex-grow-1" 
                      style={{ cursor: 'pointer' }}
                      title={`Full URL: ${getFullLink(link.linkId)}`}
                    >
                      {link.name}
                    </div>
                    {getStatusBadge(link)}
                  </div>

                  {/* Link ID */}
                  <div className="mb-3">
                    <small className="text-muted">
                      Link ID: {link.linkId}
                    </small>
                  </div>

                  {/* Usage and Created Date */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <small className="text-muted d-block">Usage</small>
                      <strong>{link.currentUses} / {link.maxUses}</strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Created</small>
                      <strong>{new Date(link.createdAt).toLocaleDateString()}</strong>
                    </div>
                  </div>

                  {/* QR Code Section */}
                  <div className="text-center mb-3">
                    {qrCodes[link.linkId] ? (
                      <div>
                        <img 
                          src={qrCodes[link.linkId]} 
                          alt={`QR Code for ${link.name}`}
                          style={{ width: '80px', height: '80px' }}
                          className="mb-2"
                        />
                        <div>
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => copyQRCodeToClipboard(link.linkId)}
                            title="Copy QR code for printing"
                            className="me-2"
                          >
                            📋 Copy QR
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => generateQRCode(link.linkId)}
                        title="Generate QR code"
                      >
                        📱 Generate QR Code
                      </Button>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => copyToClipboard(getFullLink(link.linkId))}
                      title={`Copy full URL: ${getFullLink(link.linkId)}`}
                      className="flex-grow-1"
                    >
                      📋 Copy Link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => openEditModal(link)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => openDeleteModal(link)}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Booking Link</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Link Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Airport Transfer Link"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Maximum Uses</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="1000"
                value={formData.maxUses}
                onChange={(e) => setFormData({ ...formData, maxUses: parseInt(e.target.value) })}
                required
              />
              <Form.Text className="text-muted">
                How many times can this link be used? (1-1000)
              </Form.Text>
            </Form.Group>

            <h6>Pre-fill Form Data (Optional)</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.prefillData.from}
                    onChange={(e) => setFormData({
                      ...formData,
                      prefillData: { ...formData.prefillData, from: e.target.value }
                    })}
                    placeholder="e.g., London Heathrow Airport"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>To Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.prefillData.to}
                    onChange={(e) => setFormData({
                      ...formData,
                      prefillData: { ...formData.prefillData, to: e.target.value }
                    })}
                    placeholder="e.g., Central London"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.prefillData.notes}
                onChange={(e) => setFormData({
                  ...formData,
                  prefillData: { ...formData.prefillData, notes: e.target.value }
                })}
                placeholder="Any special instructions or notes"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>What to Expect Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.prefillData.whatToExpect}
                onChange={(e) => setFormData({
                  ...formData,
                  prefillData: { ...formData.prefillData, whatToExpect: e.target.value }
                })}
                placeholder="Custom instructions for customers about what to expect (optional). Leave blank to use default instructions."
              />
              <Form.Text className="text-muted">
                These instructions will be displayed to customers when they use this booking link.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Link
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Booking Link</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEdit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Link Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Maximum Uses</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="1000"
                value={formData.maxUses}
                onChange={(e) => setFormData({ ...formData, maxUses: parseInt(e.target.value) })}
                required
              />
              <Form.Text className="text-muted">
                How many times can this link be used? (1-1000)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Current Usage</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <span className="me-2">
                  <strong>{editingLink?.currentUses || 0}</strong> / {formData.maxUses} uses
                </span>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to reset the usage counter to 0?')) {
                      try {
                        await axios.post(`/booking-links/${editingLink._id}/reset-usage`);
                        toast.success('Usage counter reset successfully');
                        loadLinks();
                        setShowEditModal(false);
                      } catch (err) {
                        console.error('Error resetting usage:', err);
                        toast.error('Failed to reset usage counter');
                      }
                    }
                  }}
                  disabled={!editingLink || editingLink.currentUses === 0}
                >
                  Reset Usage
                </Button>
              </div>
              <Form.Text className="text-muted">
                Reset the usage counter back to 0 to allow the link to be used again
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={editingLink?.isActive}
                onChange={(e) => setEditingLink({ ...editingLink, isActive: e.target.checked })}
              />
            </Form.Group>

            <h6>Pre-fill Form Data</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.prefillData.from}
                    onChange={(e) => setFormData({
                      ...formData,
                      prefillData: { ...formData.prefillData, from: e.target.value }
                    })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>To Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.prefillData.to}
                    onChange={(e) => setFormData({
                      ...formData,
                      prefillData: { ...formData.prefillData, to: e.target.value }
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.prefillData.notes}
                onChange={(e) => setFormData({
                  ...formData,
                  prefillData: { ...formData.prefillData, notes: e.target.value }
                })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>What to Expect Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.prefillData.whatToExpect}
                onChange={(e) => setFormData({
                  ...formData,
                  prefillData: { ...formData.prefillData, whatToExpect: e.target.value }
                })}
                placeholder="Custom instructions for customers about what to expect (optional). Leave blank to use default instructions."
              />
              <Form.Text className="text-muted">
                These instructions will be displayed to customers when they use this booking link.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Link
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Booking Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the booking link <strong>"{deletingLink?.name}"</strong>?</p>
          <p className="text-muted">
            This action cannot be undone. Any existing bookings from this link will remain in your diary.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Link
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
